import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Send, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatIST } from '../../lib/store';
import { DeliveryBadge, showPhone } from './WhatsAppDeliveryLog';

const Flag = ({ on, label }) => (
  <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${on ? 'bg-heda-olive-tint text-heda-olive-dark' : 'bg-heda-cream text-heda-mute line-through'}`}>{label}</span>
);

/**
 * Admin → WhatsApp → Recipients. Every active employee, the number
 * WhatsApp will actually dial (or why it can't), their bot flags, whether
 * they have messaged the business in the last 24 hours, the last delivery
 * result, and a one-tap Test send that shows DoubleTick's raw answer.
 */
export default function WhatsAppRecipients({ data, loading, onChanged }) {
  const [sendingId, setSendingId] = useState(null);
  const [result, setResult] = useState(null);

  const testSend = async (u) => {
    setSendingId(u.id);
    try {
      const res = await api.whatsappTestSend(u.id);
      setResult({ user: u, ...res.data });
      if (res.data.sent) toast.success(`DoubleTick accepted the test for ${u.name}. Check their WhatsApp.`);
      else toast.error(`Not sent to ${u.name}: ${res.data.reason || res.data.status}`, { duration: 8000 });
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingId(null);
    }
  };

  const rows = data?.recipients || [];

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="whatsapp-recipients">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-heda-gold" />
          <h3 className="font-display text-[15px] font-bold text-heda-ink">Recipients ({rows.length})</h3>
        </div>
        <p className="text-[13px] text-heda-sub">
          Every active employee and the number reminders go to. <span className="font-semibold">Test send</span> sends
          the approved template <span className="font-mono">{data?.default_test_template || 'lead reminder'}</span> with sample values
          and shows exactly what DoubleTick answered. Plain text only reaches someone who messaged the business in the last 24 hours.
        </p>
        {loading && !data ? (
          <div className="flex items-center justify-center gap-2 text-sm text-heda-mute py-8"><Loader2 className="w-4 h-4 animate-spin text-heda-gold" /> Loading…</div>
        ) : (
          <div className="border border-heda-line rounded-xl overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="heda-thead">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Name</th>
                  <th className="px-3 py-2.5 font-semibold">WhatsApp number</th>
                  <th className="px-3 py-2.5 font-semibold hidden md:table-cell">Flags</th>
                  <th className="px-3 py-2.5 font-semibold hidden sm:table-cell">Last delivery</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Test</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => {
                  const last = u.last_delivery;
                  return (
                    <tr key={u.id} className="border-t border-heda-line align-top" data-testid={`recipient-row-${u.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-heda-ink">{u.name}</div>
                        <div className="text-[11px] text-heda-mute capitalize">{u.role}</div>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {u.phone_status === 'ok' ? (
                          <span className="text-heda-ink">{showPhone(u.phone)}</span>
                        ) : (
                          <span className="text-red-700 font-semibold">
                            {u.phone_status === 'missing' ? 'missing' : `invalid: ${u.phone_typed}`}
                          </span>
                        )}
                        {u.window_open && <div className="text-[11px] text-emerald-700">messaged in last 24 h</div>}
                      </td>
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        <div className="flex flex-wrap gap-1">
                          <Flag on={u.whatsapp_enabled} label="Bot" />
                          <Flag on={u.whatsapp_walkin_enabled} label="Walk-in" />
                          <Flag on={u.whatsapp_quotation_enabled} label="Quotation" />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 hidden sm:table-cell text-[12px]">
                        {last ? (
                          <div className="space-y-0.5">
                            <div className="flex flex-wrap items-center gap-1">
                              <DeliveryBadge status={last.status} />
                              {last.delivery_status && <DeliveryBadge status={last.delivery_status} />}
                            </div>
                            <div className="text-heda-mute tabular-nums">{formatIST(last.sent_at)}</div>
                            {last.failure_reason && <div className="text-red-700 break-words max-w-xs">{last.failure_reason}</div>}
                          </div>
                        ) : <span className="text-heda-mute">No messages yet</span>}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={u.phone_status !== 'ok' || sendingId === u.id}
                          onClick={() => testSend(u)}
                          className="h-9 rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                          title={u.phone_status !== 'ok' ? 'Fix the phone number in the Staff tab first' : 'Send one test template'}
                          data-testid={`recipient-test-send-${u.id}`}
                        >
                          {sendingId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 sm:mr-1" />}
                          <span className="hidden sm:inline">Test send</span>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!result} onOpenChange={(open) => !open && setResult(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="test-send-result">
          <DialogHeader>
            <DialogTitle>Test send — {result?.user?.name}</DialogTitle>
          </DialogHeader>
          {result && (
            <div className="space-y-2 text-[12.5px]">
              <div className="flex flex-wrap items-center gap-2">
                <DeliveryBadge status={result.status} />
                <span className="tabular-nums">{showPhone(result.phone)}</span>
                <span className="font-mono text-heda-sub">{result.template}</span>
              </div>
              {result.reason && <p className="text-red-700 break-words">{result.reason}</p>}
              {result.provider_message_id && <p className="break-all">Message id: <span className="font-mono">{result.provider_message_id}</span></p>}
              <p className="heda-label">Request sent to DoubleTick (API key hidden)</p>
              <pre className="whitespace-pre-wrap break-all rounded-xl border border-heda-line bg-heda-cream/40 p-2 font-mono text-[11px] max-h-60 overflow-auto">{result.request ? JSON.stringify(result.request, null, 2) : '— not sent (see the reason above)'}</pre>
              <p className="heda-label">DoubleTick response</p>
              <pre className="whitespace-pre-wrap break-all rounded-xl border border-heda-line bg-heda-cream/40 p-2 font-mono text-[11px] max-h-60 overflow-auto">{result.response ? JSON.stringify(result.response, null, 2) : '—'}</pre>
              <p className="text-heda-mute">Accepted by DoubleTick is not the same as delivered: the Delivery log shows delivered / read when DoubleTick reports it.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
